/** System status → Logs: log level per service, and the service log viewer. */
import { Fragment, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Pause, Play } from 'lucide-react';
import { api, errorMessage } from '../../../api/client';
import { TimeRange, useTimeRange } from '../../../components/TimeRange';
import { toast } from '../../../components/Toasts';
import { TableScroll } from '../../../components/TableScroll';
import { dateTime } from '../../../lib/format';
import { safeStorage } from '../../../lib/prefs';

interface LogLine { id: number; at: string; service: string; instance: string; level: number; msg: string; data: Record<string, unknown> }
const LEVEL = { 10: 'trace', 20: 'debug', 30: 'info', 40: 'warn', 50: 'error', 60: 'fatal' } as Record<number, string>;

interface LogLevelsDTO {
  levels: string[];
  items: Array<{ target: string; label: string; setting: string | null; effective: string[]; instances: number }>;
}

const LOGGING_OPEN_KEY = 'prism.systemStatus.loggingOpen';

/** Log level per service and worker role: applied by the running services within about 15 seconds. */
export function Logging() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['log-levels'], queryFn: () => api<LogLevelsDTO>('/api/v1/system/log-levels'), refetchInterval: 10_000 });
  const [busy, setBusy] = useState(false);
  // Collapsed by default; the choice is remembered in this browser.
  const [open, setOpen] = useState(() => safeStorage.getItem(LOGGING_OPEN_KEY) === '1');
  function toggle() {
    const next = !open;
    setOpen(next);
    safeStorage.setItem(LOGGING_OPEN_KEY, next ? '1' : '0');
  }
  const d = q.data;
  async function choose(target: string, level: string) {
    if (!d) return;
    const services: Record<string, string> = {};
    for (const i of d.items) {
      const v = i.target === target ? level : i.setting;
      if (v) services[i.target] = v;
    }
    setBusy(true);
    try {
      qc.setQueryData(['log-levels'], await api<LogLevelsDTO>('/api/v1/system/log-levels', { method: 'PUT', json: { services } }));
      toast('Log level saved; services apply it within about 15 seconds');
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  if (!d) return <div className="skeleton" style={{ height: open ? 120 : 44, marginBottom: 14 }} />;
  const changed = d.items.filter((i) => i.setting).length;
  const summary = changed === 0 ? 'all Default' : `${changed} service${changed === 1 ? '' : 's'} changed`;
  return (
    <section className="panel-card" style={{ marginBottom: 14 }} data-testid="logging">
      <button type="button" className="btn ghost" style={{ width: '100%', justifyContent: 'flex-start', gap: 8, padding: 0 }} aria-expanded={open} aria-controls="logging-body" onClick={toggle} data-testid="logging-toggle">
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        <span className="section-label" style={{ margin: 0 }}>Logging</span>
        <span className="muted" data-testid="logging-summary">· {summary}</span>
      </button>
      {open && <div id="logging-body" style={{ marginTop: 10 }}>
      <TableScroll>
      <table className="table compact">
        <thead><tr><th>Service</th><th style={{ width: 200 }}>Log level</th><th>Running at</th></tr></thead>
        <tbody>{d.items.map((i) => (
          <tr key={i.target} data-testid={`log-level-${i.target}`}>
            <td className="wrap">{i.label}</td>
            <td>
              <select className="select" value={i.setting ?? ''} disabled={busy} aria-label={`Log level: ${i.label}`} onChange={(e) => void choose(i.target, e.target.value)}>
                <option value="">Default (env level)</option>
                {d.levels.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </td>
            <td className="muted wrap" data-testid="log-level-effective">
              {i.instances === 0 ? 'not running' : `${i.effective.join(', ') || '…'}${i.instances > 1 ? ` (${i.instances} instances)` : ''}`}
            </td>
          </tr>
        ))}</tbody>
      </table>
      </TableScroll>
      <span className="help">Applies within about 15 seconds, no restart. debug and trace are very verbose. Default is the LOG_LEVEL each service started with. A media worker that serves several roles uses the most verbose of its roles.</span>
      </div>}
    </section>
  );
}

export function Logs() {
  const [service, setService] = useState('');
  const [level, setLevel] = useState<'info' | 'warn' | 'error' | 'debug' | 'trace'>('info');
  const [q, setQ] = useState('');
  const [live, setLive] = useState(true);
  const [open, setOpen] = useState<number | null>(null);
  const time = useTimeRange('all');
  const params = new URLSearchParams({ level, limit: '300', ...(service ? { service } : {}), ...(q.trim() ? { q: q.trim() } : {}) });
  // A window that ends in the past has nothing new to follow.
  const following = live && !(time.value.range === 'custom' && time.value.to);
  const logs = useQuery({
    queryKey: ['system-logs', params.toString(), time.key],
    queryFn: () => {
      const w = time.window();
      const all = new URLSearchParams(params);
      if (w.from) all.set('from', w.from);
      if (w.to) all.set('to', w.to);
      return api<{ services: string[]; items: LogLine[] }>(`/api/v1/system/logs?${all}`);
    },
    refetchInterval: following ? 3000 : false,
  });
  const today = new Date().toDateString();
  const items = logs.data?.items ?? [];
  return (
    <section className="panel-card" data-testid="log-viewer">
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="select" style={{ width: 200 }} value={service} onChange={(e) => setService(e.target.value)} aria-label="Service">
          <option value="">All services</option>
          {(logs.data?.services ?? []).map((x) => <option key={x} value={x}>{x.replace(/^prism-/, '')}</option>)}
        </select>
        <select className="select" style={{ width: 140 }} value={level} onChange={(e) => setLevel(e.target.value as typeof level)} aria-label="Level">
          <option value="trace">Trace and up</option><option value="debug">Debug and up</option><option value="info">Info and up</option><option value="warn">Warnings and errors</option><option value="error">Errors</option>
        </select>
        <TimeRange value={time.value} onChange={time.setValue} />
        <input className="input" style={{ flex: 1, minWidth: 180 }} placeholder="Search messages and fields…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search logs" />
        <button className="btn sm" disabled={time.value.range === 'custom' && Boolean(time.value.to)} onClick={() => setLive(!live)}>{live ? <><Pause size={13} /> Pause</> : <><Play size={13} /> Follow</>}</button>
      </div>
      <div className="log-view">
        {items.length === 0 ? <p className="muted" style={{ padding: 10 }}>No log lines match.</p> : items.map((l) => (
          <Fragment key={l.id}>
            <div className={`log-line lvl-${LEVEL[l.level] ?? 'info'}`} onClick={() => setOpen(open === l.id ? null : l.id)} data-testid="log-line">
              <span className="mono faint" title={dateTime(l.at)}>{new Date(l.at).toDateString() === today ? '' : `${new Date(l.at).toLocaleDateString()} `}{new Date(l.at).toLocaleTimeString()}</span>
              <span className="log-level">{LEVEL[l.level] ?? l.level}</span>
              <span className="log-service">{l.service.replace(/^prism-/, '')}</span>
              <span className="log-msg">{l.msg}{Object.keys(l.data).length ? <span className="faint"> {JSON.stringify(l.data).slice(0, 160)}</span> : null}</span>
            </div>
            {open === l.id && <pre className="code-block" style={{ margin: '0 0 6px' }}>{JSON.stringify({ instance: l.instance, at: l.at, ...l.data }, null, 2)}</pre>}
          </Fragment>
        ))}
      </div>
      <span className="help">Lines at info level and above (or the level chosen under Logging) from every service that can reach the API, kept for {7} days (PRISM_LOG_RETENTION_DAYS). Request access lines are left out; the full stream is still on each service’s standard output.</span>
    </section>
  );
}
